import { useState } from "react";
import * as XLSX from "xlsx";
import "./MuffinClear.css";
import { successMuffinTransactions } from "../../../config/LiveApi";
import { useSelector } from "react-redux";

const BATCH_SIZE = 10;

const MuffinClear = () => {
  const token = useSelector((store) => store.userReducer.token);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [processedCount, setProcessedCount] = useState(0);
  const [successRows, setSuccessRows] = useState([]);
  const [failedRows, setFailedRows] = useState([]);

  /* =========================
     Upload Excel
  ========================= */
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      const workbook = XLSX.read(evt.target.result, { type: "binary" });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const data = XLSX.utils.sheet_to_json(sheet, { defval: "" });

      setRecords(data);
      setProcessedCount(0);
      setSuccessRows([]);
      setFailedRows([]);
    };

    reader.readAsBinaryString(file);
  };

  /* =========================
     Single API Call
  ========================= */
  const sendTxn = async (row, successList, failedList) => {
    try {
      const payload = {
        amount: String(row.AMOUNT),
        referenceId: String(row.REFID),
        UTR: String(row.UTR),
        VPA: row?.VPA ? String(row.VPA) : "",
      };

      const res = await successMuffinTransactions(token, payload);

      if (res.data?.success) {
        successList.push({
          ...row,
          STATUS: "SUCCESS",
          MESSAGE: res.data.message,
        });
      } else {
        failedList.push({
          ...row,
          STATUS: "FAILED",
          MESSAGE: res.data?.message || "Failed",
        });
      }
    } catch (err) {
      failedList.push({
        ...row,
        STATUS: "FAILED",
        MESSAGE: err?.response?.data?.message || err.message || "API Error",
      });
    } finally {
      setProcessedCount((prev) => prev + 1);
    }
  };

  /* =========================
     PROCESS IN BATCHES OF 10
  ========================= */
  const processAll = async () => {
    if (!records.length) {
      alert("Please upload an Excel file first");
      return;
    }

    setLoading(true);
    setProcessedCount(0);

    const localSuccess = [];
    const localFailed = [];

    for (let i = 0; i < records.length; i += BATCH_SIZE) {
      const batch = records.slice(i, i + BATCH_SIZE);

      await Promise.all(
        batch.map((row) => {
          if (!row.REFID || !row.AMOUNT || !row.UTR) {
            localFailed.push({
              ...row,
              STATUS: "FAILED",
              MESSAGE: "Missing required fields",
            });
            setProcessedCount((prev) => prev + 1);
            return Promise.resolve();
          }

          return sendTxn(row, localSuccess, localFailed);
        })
      );
    }

    setSuccessRows(localSuccess);
    setFailedRows(localFailed);
    setLoading(false);

    /* =========================
       Auto Download Excel
    ========================= */
    setTimeout(() => {
      if (localSuccess.length) {
        downloadExcel(localSuccess, "muffin_success_transactions.xlsx");
      }

      if (localFailed.length) {
        downloadExcel(localFailed, "muffin_failed_transactions.xlsx");
      }
    }, 300);
  };

  /* =========================
     Excel Download
  ========================= */
  const downloadExcel = (data, fileName) => {
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
    XLSX.writeFile(wb, fileName);
  };

  const progress =
    records.length === 0
      ? 0
      : Math.round((processedCount / records.length) * 100);

  return (
    <div className="muffin-wrapper">
      <div className="muffin-container">
        <h2 className="muffin-heading">Muffin Batch Processor</h2>

        {/* File Upload */}
        <label className="muffin-file-upload">
          <input type="file" accept=".xlsx,.xls" onChange={handleFileUpload} />
          <span>
            {records.length
              ? `${records.length} records loaded`
              : "Upload Excel File"}
          </span>
        </label>

        {/* Action Button */}
        <button
          className={`muffin-primary-btn ${loading ? "loading" : ""}`}
          onClick={processAll}
          disabled={loading}
        >
          {loading ? "Processing Batch…" : "Process Transactions"}
        </button>

        {/* Progress */}
        {records.length > 0 && (
          <div className="muffin-progress">
            <div className="muffin-progress-bar">
              <div
                className="muffin-progress-fill"
                style={{ width: `${progress}%` }}
              />
            </div>

            <p>{progress}% completed</p>
            <p>Total Records: {records.length}</p>
            <p className="success">Successful: {successRows.length}</p>
            <p className="failed">Failed: {failedRows.length}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MuffinClear;
